import { type Dispatch } from '@reduxjs/toolkit';
import { type Stripe as StripeType } from '@stripe/stripe-js';
import { sendAnalyticsEvents } from '../../analytics';
import { EVENTS, PAYMENT_GATEWAY, PAYMENT_GATEWAY_PROPERTY } from '../../core/constants';
import { setRequestResult, setShowPaymentError } from '../../core/store/checkout';
import { setEventData } from '../../core/store/events';
import { setShowLoader } from '../../core/store/loader';
import { getPollingID, type StripePollingOptions } from '../../core/store/checkoutFetch';
import { isValidPixHolderName, normalizeBrazilTaxIdForStripe, pixEmail } from './brazilTaxId';
import { completeStripePaymentOnBackend } from '../stripePayment/backendCompletion';
import { resolveStripePaymentStart } from '../stripePayment/startResult';

const pixPollingOptions = (email: string): StripePollingOptions => ({
  alternatePaymentMethod: 'pix_automatico',
  email,
});

export const PIX_PENDING_STORAGE_KEY = 'pix_pending';

export interface PixPurchaseProps {
  stripe: StripeType;
  plan: string;
  token: string;
  analyticsParams: any;
  eventsData: any;
  successPayment?: (id1: string, id2: string) => void;
  failedPayment?: () => void;
  coupon?: string;
  email?: string;
  returnUrl: string;
  billingDetails?: { name?: string; email?: string; tax_id?: string };
  stripePublishableKey: string;
}

export type CompletePixReturnPending = {
  plan: string;
  token: string;
  analyticsParams: any;
  eventsData: any;
  coupon?: string;
  email?: string;
};

export type PixPendingPayload = CompletePixReturnPending & {
  stripePublishableKey: string;
  tax_id: string;
  holder_name: string;
};

export const pixPurchase =
  ({
    stripe,
    plan,
    token,
    analyticsParams,
    eventsData,
    successPayment,
    failedPayment,
    coupon,
    email = '',
    returnUrl,
    billingDetails,
    stripePublishableKey,
  }: PixPurchaseProps) =>
  async (dispatch: Dispatch) => {
    const userEmail = pixEmail(email, billingDetails, eventsData, analyticsParams);

    const onError = (data: any, errorData?: any) => {
      failedPayment?.();
      let txt = data;
      if (typeof txt !== 'string') txt = data?.message;
      if (typeof txt !== 'string') txt = 'Payment error';
      dispatch(
        setEventData({
          declineCode: errorData?.decline_code || errorData?.processor_response,
        }),
      );
      sendAnalyticsEvents(EVENTS.PAYMENT_DECLINE, {
        ...eventsData,
        declineCode: errorData?.decline_code || errorData?.processor_response,
      });
      dispatch(setShowPaymentError(txt));
      dispatch(setShowLoader(false));
    };
    const onSuccess = (
      sub_id: string,
      event_id: string,
      paymentData?: { customer_id?: string | number },
    ) => {
      successPayment?.(sub_id, event_id);
      dispatch(setRequestResult({ isSuccess: true, id: sub_id }));
      sendAnalyticsEvents(EVENTS.SUBSCRIBED, {
        ...eventsData,
        ...(paymentData?.customer_id != null && paymentData.customer_id !== ''
          ? { customer_id: paymentData.customer_id }
          : {}),
      });
      dispatch(setShowLoader(false));
    };

    dispatch(setShowLoader(true));

    try {
      const pixResponse = await getPollingID(
        '',
        plan,
        token,
        analyticsParams,
        eventsData,
        (err: any) => onError(err?.message || err),
        '',
        coupon,
        pixPollingOptions(userEmail),
      );

      if (!pixResponse || typeof pixResponse !== 'object') {
        dispatch(setShowLoader(false));
        return;
      }

      const initial = await resolveStripePaymentStart(pixResponse as Record<string, unknown>, {
        couldNotStart: 'Could not start PIX payment',
        paymentFailed: 'An error has occurred',
      });

      if (initial.kind === 'failed') {
        onError(initial.message, initial.paymentData ?? initial.errorData);
        return;
      }
      if (initial.kind === 'completed') {
        onSuccess(initial.subscriptionId, initial.eventId, {
          customer_id: initial.customerId,
        });
        return;
      }

      const paymentIntentSecret = initial.clientSecret;

      const stripeAny = stripe as any;
      const confirmPix = stripeAny.confirmPixPayment;
      if (typeof confirmPix !== 'function') {
        onError('PIX payment is not supported', undefined);
        return;
      }

      const taxIdNorm = normalizeBrazilTaxIdForStripe(billingDetails?.tax_id || '');
      if (!taxIdNorm) {
        onError('Please enter a valid CPF or CNPJ for PIX.', {
          code: 'tax_id_invalid',
        });
        return;
      }
      const holderNameTrimmed = (billingDetails?.name || '').trim();
      if (!isValidPixHolderName(holderNameTrimmed)) {
        onError('Please enter a valid name for PIX.', {
          code: 'pix_name_invalid',
        });
        return;
      }

      localStorage.setItem(
        PIX_PENDING_STORAGE_KEY,
        JSON.stringify({
          plan,
          token,
          analyticsParams,
          eventsData,
          coupon,
          email: userEmail,
          stripePublishableKey,
          tax_id: taxIdNorm,
          holder_name: holderNameTrimmed,
        }),
      );

      const confirmParams: {
        return_url: string;
        payment_method: {
          billing_details: { name: string; email?: string; tax_id: string };
        };
      } = {
        return_url: returnUrl,
        payment_method: {
          billing_details: {
            name: holderNameTrimmed,
            ...(userEmail && { email: userEmail }),
            tax_id: taxIdNorm,
          },
        },
      };

      const result = await confirmPix(paymentIntentSecret, confirmParams);

      const err = result?.error;
      const paymentIntent = result?.paymentIntent;

      if (err) {
        localStorage.removeItem(PIX_PENDING_STORAGE_KEY);
        onError(err.message, err);
        return;
      }

      if (paymentIntent?.id) {
        localStorage.removeItem(PIX_PENDING_STORAGE_KEY);
        const completion = await completeStripePaymentOnBackend({
          paymentIntentId: paymentIntent.id,
          context: {
            plan,
            token,
            analyticsParams,
            eventsData,
            coupon,
          },
          pollingOptions: pixPollingOptions(userEmail),
        });

        if (completion.kind === 'failed') {
          onError(completion.message, completion.paymentData ?? completion.errorData);
          return;
        }

        onSuccess(completion.subscriptionId, completion.eventId, {
          customer_id: completion.customerId,
        });
      }
    } catch (err: any) {
      localStorage.removeItem(PIX_PENDING_STORAGE_KEY);
      onError(err?.message || 'Payment error', undefined);
    } finally {
      dispatch(setShowLoader(false));
    }
  };

export const completePixReturn =
  ({
    stripe,
    clientSecret,
    pending,
    onSuccess,
    onError,
  }: {
    stripe: StripeType;
    clientSecret: string;
    pending: CompletePixReturnPending;
    onSuccess: (sub_id: string, event_id: string) => void;
    onError: (message: string) => void;
  }) =>
  async (dispatch: Dispatch) => {
    const { plan, token, analyticsParams, eventsData, coupon, email: storedEmail } = pending;
    const paymentGateway =
      eventsData?.[PAYMENT_GATEWAY_PROPERTY] ||
      PAYMENT_GATEWAY.stripe(eventsData?.stripe_account_name, 'PIX');
    const userEmail = pixEmail(storedEmail, undefined, eventsData, analyticsParams);
    dispatch(setShowLoader(true));
    try {
      const { paymentIntent, error } = await stripe.retrievePaymentIntent(clientSecret);
      if (error) {
        onError(error.message || 'Could not retrieve payment');
        return;
      }
      if (!paymentIntent?.id) {
        onError('Invalid payment intent');
        return;
      }
      const completion = await completeStripePaymentOnBackend({
        paymentIntentId: paymentIntent.id,
        context: {
          plan,
          token,
          analyticsParams,
          eventsData,
          coupon,
        },
        pollingOptions: pixPollingOptions(userEmail),
      });

      if (completion.kind === 'completed') {
        dispatch(
          setRequestResult({
            isSuccess: true,
            id: completion.subscriptionId,
          }),
        );
        sendAnalyticsEvents(EVENTS.SUBSCRIBED, {
          ...eventsData,
          [PAYMENT_GATEWAY_PROPERTY]: paymentGateway,
          ...(completion.customerId != null && completion.customerId !== ''
            ? { customer_id: completion.customerId }
            : {}),
        });
        onSuccess(completion.subscriptionId, completion.eventId);
      } else {
        onError(completion.message);
      }
    } catch (err: any) {
      onError(err?.message || 'Payment error');
    } finally {
      dispatch(setShowLoader(false));
    }
  };
